// src/components/ForecastsTab.jsx
// "Forecasts" is the user-facing name for all tenants. The backing data keeps
// its original names (`predictions` table, predictions_enabled config). The
// word "Predictions" was once reserved for a separate feature — that shipped
// as "Prediction" (city tenants) and has since been renamed to "Analysis"
// (see src/components/AnalysisTab.jsx); the bare word is no longer reserved.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import SubscribeGate from '../auth/SubscribeGate';
import DividedVotes from './DividedVotes';
import CausesPanel from './CausesPanel';
import ForecastIntel from './ForecastIntel';
import AmendmentsPanel from './AmendmentsPanel';
import './ForecastsTab.css';

/* ── Kind metadata (badge label + color class) ── */
const KIND_META = {
  vote_outcome:     { label: 'Vote Outcome',     cls: 'pred-kind-vote' },
  member_alignment: { label: 'Member Alignment', cls: 'pred-kind-members' },
  budget:           { label: 'Budget',           cls: 'pred-kind-budget' },
  policy:           { label: 'Policy',           cls: 'pred-kind-policy' },
  trend:            { label: 'Trend',            cls: 'pred-kind-trend' },
};

// 'member_alignment' is the DB kind; "Opinions" is its user-facing name.
// 'all' is labelled "Outlook": it no longer just lists everything — it leads
// with the derived funding outlook, pushback forecast, and the proposal
// estimator, then the model forecast cards. 'causes' and 'amendments' are not
// prediction kinds — they select record-derived analyses and are filtered out
// of the prediction card list below.
const KIND_FILTERS = [
  { id: 'all',              label: 'Outlook' },
  { id: 'vote_outcome',     label: 'Votes' },
  { id: 'member_alignment', label: 'Opinions' },
  { id: 'causes',           label: 'Causes' },
  { id: 'amendments',       label: 'Amendments' },
  { id: 'budget',           label: 'Budget' },
  { id: 'policy',           label: 'Policy' },
  { id: 'trend',            label: 'Trends' },
];

/* ── Helpers ── */
const parseJson = (v) => {
  if (!v) return null;
  if (typeof v === 'string') {
    try { return JSON.parse(v); } catch { return null; }
  }
  return v;
};

// 'YYYY-MM-DD' (date-only) -> "June 24, 2025" with no timezone drift.
const formatDateOnly = (iso) => {
  if (!iso) return '';
  const parts = String(iso).slice(0, 10).split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !n)) return String(iso);
  const [y, m, d] = parts;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
};

// Full timestamp -> "June 24, 2025"
const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const normVoteClass = (v) => {
  const val = String(v || '').toLowerCase();
  if (val === 'yes') return 'pred-vote-yes';
  if (val === 'no') return 'pred-vote-no';
  if (val === 'abstain') return 'pred-vote-abstain';
  return 'pred-vote-unknown'; // absent / unknown / anything else -> gray
};

const voteLabel = (v) => {
  const val = String(v || '').toLowerCase();
  if (['yes', 'no', 'abstain', 'absent'].includes(val)) {
    return val.charAt(0).toUpperCase() + val.slice(1);
  }
  return 'Unknown';
};

const confDotClass = (c) => {
  const val = String(c || '').toLowerCase();
  if (val === 'high') return 'pred-conf-dot pred-conf-dot-high';
  if (val === 'medium') return 'pred-conf-dot pred-conf-dot-medium';
  return 'pred-conf-dot pred-conf-dot-low';
};

/* ── Single forecast card ── */
function ForecastCard({ row }) {
  const kind = KIND_META[row.kind] || { label: row.kind || 'Forecast', cls: 'pred-kind-trend' };
  const pred = parseJson(row.prediction) || {};
  const probability =
    typeof pred.probability === 'number' && Number.isFinite(pred.probability)
      ? Math.max(0, Math.min(1, pred.probability))
      : null;
  const memberPreds = Array.isArray(pred.member_predictions) ? pred.member_predictions : [];
  const keyDrivers = Array.isArray(pred.key_drivers) ? pred.key_drivers : [];
  const confidence = String(row.confidence || '').toLowerCase();

  return (
    <div className="pred-card pred-item">
      <div className="pred-item-top">
        <span className={`pred-kind-badge ${kind.cls}`}>{kind.label}</span>
        {row.category && <span className="pred-category-tag">{row.category}</span>}
        {confidence && (
          <span className={`pred-conf-chip pred-conf-${confidence}`}>
            {confidence} confidence
          </span>
        )}
      </div>

      <h3 className="pred-item-title">{row.title}</h3>
      {row.description && <p className="pred-item-desc">{row.description}</p>}

      {pred.predicted_outcome && (
        <p className="pred-outcome-line">
          Predicted outcome: <strong>{pred.predicted_outcome}</strong>
        </p>
      )}

      {probability !== null && (
        <div className="pred-prob-row">
          <div className="pred-prob-track">
            <div
              className="pred-prob-fill"
              style={{ width: `${Math.round(probability * 100)}%` }}
            />
          </div>
          <span className="pred-prob-label">{Math.round(probability * 100)}%</span>
        </div>
      )}

      {memberPreds.length > 0 && (
        <div className="pred-members">
          {memberPreds.map((m, i) => (
            <div
              key={i}
              className="pred-member-chip"
              title={m?.rationale || undefined}
            >
              <span className="pred-member-name">{m?.name || 'Member'}</span>
              <span className={`pred-member-vote ${normVoteClass(m?.predicted_vote)}`}>
                {voteLabel(m?.predicted_vote)}
              </span>
              <span
                className={confDotClass(m?.confidence)}
                title={m?.confidence ? `Confidence: ${m.confidence}` : undefined}
              />
            </div>
          ))}
        </div>
      )}

      {keyDrivers.length > 0 && (
        <ul className="pred-drivers">
          {keyDrivers.map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      )}

      {row.rationale && (
        <details className="pred-rationale">
          <summary>Why the model expects this</summary>
          <p>{row.rationale}</p>
        </details>
      )}

      {row.horizon_date && (
        <div className="pred-horizon">Expected by {formatDateOnly(row.horizon_date)}</div>
      )}
    </div>
  );
}

/* ── Gated content: only mounted when SubscribeGate renders children,
      so the predictions query never fires for non-subscribers of a
      subscription-gated tenant. ── */
function ForecastsContent({ districtId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kindFilter, setKindFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('predictions')
        .select('*')
        .eq('districtId', districtId)
        .eq('status', 'open')
        .order('generated_at', { ascending: false });
      if (cancelled) return;
      if (err) setError(err);
      else setRows(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [districtId]);

  const filtered = useMemo(
    () => (kindFilter === 'all' ? rows : rows.filter((r) => r.kind === kindFilter)),
    [rows, kindFilter]
  );

  const newest = rows[0] || null;

  if (loading) {
    return <div className="pred-card pred-status">Loading forecasts…</div>;
  }
  if (error) {
    return <div className="pred-card pred-status pred-status-error">Error loading forecasts: {error.message}</div>;
  }

  return (
    <>
      <div className="pred-card pred-header-card">
        <div className="pred-header-row">
          <div className="pred-header-text">
            <h2 className="pred-title">Forecasts</h2>
            {newest && (
              <p className="pred-updated">
                Updated {formatDateTime(newest.generated_at)}
              </p>
            )}
          </div>
          <div className="pred-filters">
            {KIND_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`pred-filter-chip ${kindFilter === f.id ? 'active' : ''}`}
                onClick={() => setKindFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <p className="pred-honesty-note">
          Forecasts are based on public meeting records, voting history, and public
          comments. They are estimates, not statements of fact, and are not affiliated
          with or endorsed by the district or council.
        </p>
      </div>

      {/* Opinions leads with the factual record — how members actually split,
          and why — because member forecasts only exist when a run produces
          them, and the voting history answers the same question from evidence
          rather than estimate. */}
      {kindFilter === 'member_alignment' && (
        <DividedVotes districtId={districtId} />
      )}

      {/* Causes and Amendments are derived from the record, not the
          predictions table. */}
      {kindFilter === 'causes' && <CausesPanel districtId={districtId} />}
      {kindFilter === 'amendments' && <AmendmentsPanel districtId={districtId} />}

      {/* "Outlook" leads with the derived intelligence — funding cycles,
          likely pushback, and the test-a-proposal estimator — then the model
          forecast cards below. */}
      {kindFilter === 'all' && <ForecastIntel districtId={districtId} />}

      {(kindFilter === 'causes' || kindFilter === 'amendments') ? null : rows.length === 0 ? (
        kindFilter === 'member_alignment' ? null : (
          <div className="pred-card pred-empty">
            No open forecasts yet. Forecasts are generated after each new meeting is processed.
          </div>
        )
      ) : filtered.length === 0 ? (
        kindFilter === 'member_alignment' ? (
          <div className="pred-card pred-empty">
            No open member forecasts in the latest run. The voting record above is
            the factual account of how members have split.
          </div>
        ) : (
          <div className="pred-card pred-empty">No open forecasts match this filter.</div>
        )
      ) : (
        <div className="pred-list">
          {filtered.map((row) => (
            <ForecastCard key={row.id} row={row} />
          ))}
        </div>
      )}
    </>
  );
}

/* ── Tab entry point: district lookup, then the subscription gate ── */
function ForecastsTab() {
  const { abbreviation } = useParams();

  const [district, setDistrict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setDistrict(null);
      const { data, error: err } = await supabase
        .from('Districts')
        .select('id, premium_gate')
        .eq('abbreviation', abbreviation)
        .single();
      if (cancelled) return;
      if (err || !data) setError(err || { message: 'District not found' });
      else setDistrict(data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [abbreviation]);

  if (loading) {
    return (
      <div className="pred-tab">
        <div className="pred-card pred-status">Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="pred-tab">
        <div className="pred-card pred-status pred-status-error">Error: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="pred-tab">
      <SubscribeGate premiumGate={district?.premium_gate} featureName="Forecasts">
        <ForecastsContent districtId={district.id} />
      </SubscribeGate>
    </div>
  );
}

export default ForecastsTab;
